// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { yesNo, yesNoPress } from '../../../bygning/fields';
import { shownChoice } from '../../../kortlaegning/cellDraft';
import { SegmentedControl } from '../../SegmentedControl';
import { usePendingChoice } from '../../kortlaegning/usePendingChoice';

type Answer = 'ja' | 'nej';

export interface YesNoProps {
  label: string;
  /** The stored wire value ('true', 'false', blank). */
  value: string | null;
  disabled?: boolean;
  /** The key may be blank: pressing the shown answer again clears it. */
  clearable?: boolean;
  /** The wire value: 'true', 'false' or null (cleared). */
  onChange: (value: string | null) => void | Promise<void>;
}

/**
 * A Ja/Nej pair (v8 `yn()`); `aria-pressed` carries the answer. The pressed
 * answer shows at once, while its write is in flight, and the next press is
 * judged against it, so a quick double "Ja" sends true then clear — never
 * 'true' twice (b48394a).
 */
export function YesNo({ label, value: stored, disabled, clearable = true, onChange }: YesNoProps) {
  const { pending, choose } = usePendingChoice(onChange);
  const value = yesNo(shownChoice(stored, pending));
  const press = (answer: boolean) => {
    const next = yesNoPress(stored, pending, answer, clearable);
    if (next !== undefined) choose(next);
  };
  // role="group" (aria-pressed), not radiogroup: pressing the shown answer again clears it,
  // which relies on onChange firing for the active segment too.
  return (
    <SegmentedControl<Answer>
      role="group"
      aria-label={label}
      items={[
        { value: 'ja', label: 'Ja', disabled },
        { value: 'nej', label: 'Nej', disabled },
      ]}
      value={value === true ? 'ja' : value === false ? 'nej' : null}
      onChange={(v) => press(v === 'ja')}
    />
  );
}
